import { Checkbox, ConfigProvider, Segmented, Select } from 'antd';
import {
  NUTRITION_BALANCE_HELP,
  NUTRITION_INVALID_SCOPE_MESSAGE,
  NUTRITION_NO_NUTRIENTS_SELECTED_MESSAGE,
  NUTRITION_NO_PADDOCK_DATA_MESSAGE,
  NUTRITION_NO_PLANS_MESSAGE,
  NUTRITION_RATE_EDIT_HINT,
} from 'constants/messageConstants';
import {
  ANALYSIS_PRODUCTION_EXPECTED,
  ANALYSIS_PRODUCTION_LOW,
  ANALYSIS_PRODUCTION_POTENTIAL,
  FARM_DIAGNOSTICS_TIME_RANGE_ALL,
  FARM_DIAGNOSTICS_TIME_RANGE_LABELS,
  FARM_DIAGNOSTICS_TIME_RANGE_LAST_3,
  FARM_DIAGNOSTICS_TIME_RANGE_LAST_5,
  NUTRITION_CHART_APPLIED_VS_REMOVED_TITLE,
  NUTRITION_CHART_AREA_BY_STATUS_TITLE,
  NUTRITION_CHART_PANEL_DEFAULT_WIDTH_REM,
  NUTRITION_CHART_PANEL_KEY_STEP_REM,
  NUTRITION_CHART_PANEL_MAX_WIDTH_REM,
  NUTRITION_CHART_PANEL_MIN_WIDTH_REM,
  NUTRITION_CHART_PANEL_RESIZE_LABEL,
  NUTRITION_CHART_RUNNING_BALANCE_TITLE,
  NUTRITION_CHART_WIDTH_STORAGE_KEY,
  NUTRITION_CHARTS_HIDE_LABEL,
  NUTRITION_CHARTS_LABEL,
  NUTRITION_CHARTS_OPEN_STORAGE_KEY,
  NUTRITION_CHARTS_SHOW_LABEL,
  NUTRITION_DATA_BASIS_LABEL,
  NUTRITION_FARM_SECTION_TITLE,
  NUTRITION_FARM_VIEW_BALANCE,
  NUTRITION_FARM_VIEW_LABELS,
  NUTRITION_FARM_VIEW_SEASONS,
  NUTRITION_NUTRIENT_LABEL,
  NUTRITION_NUTRIENTS_LABEL,
  NUTRITION_PADDOCK_SECTION_TITLE,
  NUTRITION_SEASON_RANGE_THIS_YEAR,
  NUTRITION_SEASON_RANGE_THIS_YEAR_LABEL,
  NUTRITION_ACTUAL_YIELD_LABEL,
  NUTRITION_SEASONS_LABEL,
  NUTRITION_SHOW_CROP_DETAILS_LABEL,
  NUTRITION_SHOW_LABEL,
  NUTRITION_YIELD_LABELS,
  NUTRITION_YIELD_SCENARIO_LABEL,
  NUTRITION_YIELD_SCENARIO_LABELS,
  SUMMARY_DATA_BASIS_ACTUAL,
  SUMMARY_DATA_BASIS_LABELS,
  SUMMARY_DATA_BASIS_PLAN,
  TREE_YEAR_ALL,
} from 'constants/uiConstants';
import type { IFarm } from 'interfaces/IFarm';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IInput } from 'interfaces/IInput';
import { InputTaskCategory } from 'interfaces/IInputTask';
import ResizeHandle from 'components/common/ResizeHandle';
import { useResizablePanel } from 'hooks/common/useResizablePanel';
import Button from 'components/common/Button';
import React, { useEffect, useMemo, useState } from 'react';
import { LuChevronsLeft, LuChevronsRight, LuInfo } from 'react-icons/lu';
import { useDispatch, useSelector } from 'react-redux';
import { getInputYear } from 'utils/FarmUtils';
import {
  setNutritionCategory,
  setNutritionFarmView,
  setNutritionNutrients,
  setNutritionSeasonRange,
  setNutritionShowCropDetails,
  setNutritionTrendNutrient,
  setNutritionYieldScenario,
  type AnalysisProductionScenario,
  type NutritionFarmView,
  type NutritionSeasonRange,
} from '../../../../../redux/slices/InsightsSlice';
import type { RootState } from '../../../../../redux/Store';
import WorkspaceEmptyState from '../../WorkspaceEmptyState';
import {
  NUTRIENT_DISPLAY_ORDER,
  NUTRIENT_LABELS,
  type NutrientKey,
} from '../computeLiveTotals';
import type { InsightsScopeLevel } from '../insightUtils';
import SectionCard from '../summary2/SectionCard';
import {
  computeNutritionAudit,
  computeSeasonTotals,
  NUTRIENT_REMOVAL_KEY,
  type PaddockNutritionAudit,
} from './computeNutritionAudit';
import FarmNutritionTable from './FarmNutritionTable';
import IconTip from './IconTip';
import {
  AppliedRemovedBySeasonChart,
  AreaByStatusChart,
  RunningBalanceChart,
} from './NutritionCharts';
import PaddockNutritionTable from './PaddockNutritionTable';
import { useNutritionRateEdit } from './useNutritionRateEdit';

/** Always shown in the single-paddock audit, data or not — the nutrients advisors check first. */
const CORE_NUTRIENTS: NutrientKey[] = [
  'nutrientN',
  'nutrientP',
  'nutrientK',
  'nutrientS',
];

const CATEGORY_OPTIONS = [
  {
    label: SUMMARY_DATA_BASIS_LABELS[SUMMARY_DATA_BASIS_PLAN],
    value: InputTaskCategory.Plan,
  },
  {
    label: SUMMARY_DATA_BASIS_LABELS[SUMMARY_DATA_BASIS_ACTUAL],
    value: InputTaskCategory.Actual,
  },
];

// Same control and labels as the Farm diagnostics time range, plus this year.
const SEASON_RANGE_OPTIONS: { value: NutritionSeasonRange; label: string }[] =
  [
    {
      value: NUTRITION_SEASON_RANGE_THIS_YEAR,
      label: NUTRITION_SEASON_RANGE_THIS_YEAR_LABEL,
    },
    ...[
      FARM_DIAGNOSTICS_TIME_RANGE_LAST_3,
      FARM_DIAGNOSTICS_TIME_RANGE_LAST_5,
      FARM_DIAGNOSTICS_TIME_RANGE_ALL,
    ].map((value) => ({
      value,
      label: FARM_DIAGNOSTICS_TIME_RANGE_LABELS[value],
    })),
  ];

const SEASON_RANGE_COUNT: Record<NutritionSeasonRange, number | null> = {
  [NUTRITION_SEASON_RANGE_THIS_YEAR]: 1,
  [FARM_DIAGNOSTICS_TIME_RANGE_LAST_3]: 3,
  [FARM_DIAGNOSTICS_TIME_RANGE_LAST_5]: 5,
  // From the earliest season any plan in scope starts in.
  [FARM_DIAGNOSTICS_TIME_RANGE_ALL]: null,
};

const YIELD_SCENARIO_OPTIONS = [
  ANALYSIS_PRODUCTION_LOW,
  ANALYSIS_PRODUCTION_EXPECTED,
  ANALYSIS_PRODUCTION_POTENTIAL,
].map((value) => ({ value, label: NUTRITION_YIELD_SCENARIO_LABELS[value] }));

const FARM_VIEW_OPTIONS = [
  NUTRITION_FARM_VIEW_BALANCE,
  NUTRITION_FARM_VIEW_SEASONS,
].map((value) => ({ label: NUTRITION_FARM_VIEW_LABELS[value], value }));

const NUTRIENT_OPTIONS = NUTRIENT_DISPLAY_ORDER.map((key) => ({
  label: NUTRIENT_LABELS[key],
  value: key,
}));

// The lens switcher theme (Insights, Farm diagnostics, plan preview) — the
// farm view switch shows one view of the same grid at a time. Selected
// label colour comes from .insights-lens-segmented in App.css.
const LENS_SWITCHER_THEME = {
  components: {
    Segmented: {
      trackBg: 'transparent',
      trackPadding: 2,
      itemSelectedBg: 'var(--color-dark-gray)',
      itemSelectedColor: 'var(--color-white)',
    },
  },
};

const clampChartWidth = (rem: number) =>
  Math.min(
    NUTRITION_CHART_PANEL_MAX_WIDTH_REM,
    Math.max(NUTRITION_CHART_PANEL_MIN_WIDTH_REM, rem)
  );

/** The chart panel width this viewer last chose — a per-browser convenience, so storage failures fall back to the default. */
const loadChartWidth = (): number => {
  try {
    const stored = Number(
      localStorage.getItem(NUTRITION_CHART_WIDTH_STORAGE_KEY)
    );
    return stored > 0
      ? clampChartWidth(stored)
      : NUTRITION_CHART_PANEL_DEFAULT_WIDTH_REM;
  } catch {
    return NUTRITION_CHART_PANEL_DEFAULT_WIDTH_REM;
  }
};

/**
 * The audit tables sit straight on the page under a heading, not in a card:
 * they're a workspace you edit in, and cards here are kept for read-only
 * charts (SectionCard `result`).
 */
const WorkspaceSection: React.FC<{
  id: string;
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}> = ({ id, title, subtitle, actions, children }) => (
  <section id={id} className="scroll-mt-24">
    <div className="mb-1 flex flex-wrap items-center gap-2">
      <h3 className="text-lg font-bold text-charcoal">{title}</h3>
      {actions}
    </div>
    {subtitle && <p className="mb-3 text-sm text-gray-dark">{subtitle}</p>}
    {children}
  </section>
);

/** Whether this viewer last left the charts open — a per-browser convenience; open by default. */
const loadChartsOpen = (): boolean => {
  try {
    return localStorage.getItem(NUTRITION_CHARTS_OPEN_STORAGE_KEY) !== 'false';
  } catch {
    return true;
  }
};

const ControlLabel: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <span className="text-sm font-semibold text-gray-dark">{children}</span>;

/** Core nutrients, plus any other nutrient the paddock applied or removed in range — the rest would be columns of dashes. */
const resolvePaddockNutrients = (
  paddock: PaddockNutritionAudit
): NutrientKey[] =>
  NUTRIENT_DISPLAY_ORDER.filter(
    (key) =>
      CORE_NUTRIENTS.includes(key) ||
      paddock.applied[key] > 0 ||
      paddock.removed[key] > 0
  );

interface NutritionViewProps {
  /** Plans in tree scope across every year — the lens picks its own seasons. */
  inputs: IInput[];
  farms: IFarm[];
  gUnits: GUnit[];
  scopeLevel?: InsightsScopeLevel;
}

/**
 * Nutrition lens (OP-2508) — nutrients applied and removed over a range of
 * seasons, and whether each paddock is building or declining. A paddock or
 * plan in the tree shows the single-paddock audit; a farm or the org shows
 * the paddock balance grid. Rates can be changed in Plan mode.
 */
const NutritionView: React.FC<NutritionViewProps> = ({
  inputs,
  farms,
  gUnits,
  scopeLevel,
}) => {
  const dispatch = useDispatch();
  const {
    nutritionCategory,
    nutritionSeasonRange,
    nutritionYieldScenario,
    nutritionNutrients,
    nutritionFarmView,
    nutritionTrendNutrient,
    nutritionShowCropDetails,
  } = useSelector((state: RootState) => state.insightsReducer);
  const { treeYearFilter, selectedPaddockId } = useSelector(
    (state: RootState) => state.farmReducer
  );

  const isPaddockScope = scopeLevel === 'paddock' || scopeLevel === 'input';
  const isFarmScope = scopeLevel === 'farm' || scopeLevel === 'org';

  // The range ends at the tree year. The tree's "All years" has no single
  // year, so it ends at the latest season any plan in scope starts in.
  const seasons = useMemo(() => {
    const years = inputs
      .map(getInputYear)
      .filter((year): year is number => year != null);
    const currentYear = new Date().getFullYear();
    const endYear =
      treeYearFilter !== TREE_YEAR_ALL
        ? treeYearFilter
        : years.length > 0
          ? Math.max(...years)
          : currentYear;
    const count = SEASON_RANGE_COUNT[nutritionSeasonRange];
    const earliest = years.length > 0 ? Math.min(...years) : endYear;
    const startYear =
      count != null ? endYear - count + 1 : Math.min(earliest, endYear);
    return Array.from(
      { length: endYear - startYear + 1 },
      (_, i) => startYear + i
    );
  }, [inputs, treeYearFilter, nutritionSeasonRange]);

  // Yield scenarios are a planning choice — Actual production records one
  // yield, so Actual always uses it.
  const isPlan = nutritionCategory === InputTaskCategory.Plan;
  const yieldScenario = isPlan
    ? nutritionYieldScenario
    : ANALYSIS_PRODUCTION_EXPECTED;
  const yieldLabel = isPlan
    ? NUTRITION_YIELD_LABELS[yieldScenario]
    : NUTRITION_ACTUAL_YIELD_LABEL;

  const audit = useMemo(
    () =>
      computeNutritionAudit({
        inputs,
        farms,
        gUnits,
        categories: new Set([nutritionCategory]),
        seasons,
        yieldScenario,
      }),
    [inputs, farms, gUnits, nutritionCategory, seasons, yieldScenario]
  );

  const {
    panelRef: chartPanelRef,
    width: chartWidth,
    setWidth: setChartWidth,
    onMouseDown: onChartResizeStart,
  } = useResizablePanel({
    min: NUTRITION_CHART_PANEL_MIN_WIDTH_REM,
    max: NUTRITION_CHART_PANEL_MAX_WIDTH_REM,
    initial: loadChartWidth(),
  });

  // Once a drag commits, hand the width back to the CSS variable — the hook
  // writes an inline width while dragging, which would otherwise stick when
  // the screen narrows and the charts move under the table.
  useEffect(() => {
    if (chartPanelRef.current) chartPanelRef.current.style.width = '';
    try {
      localStorage.setItem(NUTRITION_CHART_WIDTH_STORAGE_KEY, String(chartWidth));
    } catch {
      // Remembering the width is a convenience; the panel works without it.
    }
  }, [chartPanelRef, chartWidth]);

  const [chartsOpen, setChartsOpen] = useState(loadChartsOpen);
  useEffect(() => {
    try {
      localStorage.setItem(NUTRITION_CHARTS_OPEN_STORAGE_KEY, String(chartsOpen));
    } catch {
      // Remembering this is a convenience; the toggle works without it.
    }
  }, [chartsOpen]);

  const edit = useNutritionRateEdit(inputs, isPlan);
  const tableSubtitle = edit.canEdit ? NUTRITION_RATE_EDIT_HINT : undefined;

  if (!isPaddockScope && !isFarmScope) {
    return <WorkspaceEmptyState message={NUTRITION_INVALID_SCOPE_MESSAGE} />;
  }

  // A plan can link to more than one paddock, so pick the selected one
  // rather than whichever the audit happens to list first.
  const scopePaddocks = audit.farms.flatMap((farm) => farm.paddocks);
  const paddock: PaddockNutritionAudit | null = isPaddockScope
    ? (scopePaddocks.find((p) => p.paddockId === selectedPaddockId) ??
      scopePaddocks[0] ??
      null)
    : null;
  const paddockNutrients = paddock
    ? resolvePaddockNutrients(paddock)
    : CORE_NUTRIENTS;
  const hasAnyPaddock = scopePaddocks.length > 0;

  const helpTip = (
    <IconTip title={NUTRITION_BALANCE_HELP}>
      <LuInfo size={16} aria-hidden />
    </IconTip>
  );

  const renderMain = () => {
    if (isPaddockScope) {
      if (!paddock || paddock.seasons.length === 0) {
        return <WorkspaceEmptyState message={NUTRITION_NO_PLANS_MESSAGE} />;
      }
      return (
        <WorkspaceSection
          id="nutrition-paddock-audit"
          title={`${NUTRITION_PADDOCK_SECTION_TITLE} · ${paddock.paddockName}`}
          subtitle={tableSubtitle}
          actions={helpTip}
        >
          <PaddockNutritionTable
            paddock={paddock}
            nutrients={paddockNutrients}
            seasons={audit.seasons}
            edit={edit}
            yieldLabel={yieldLabel}
          />
        </WorkspaceSection>
      );
    }

    if (!hasAnyPaddock) {
      return <WorkspaceEmptyState message={NUTRITION_NO_PADDOCK_DATA_MESSAGE} />;
    }
    if (
      nutritionFarmView === NUTRITION_FARM_VIEW_BALANCE &&
      nutritionNutrients.length === 0
    ) {
      return (
        <WorkspaceEmptyState message={NUTRITION_NO_NUTRIENTS_SELECTED_MESSAGE} />
      );
    }
    return (
      <div className="flex flex-col gap-3">
        <ConfigProvider theme={LENS_SWITCHER_THEME}>
          <Segmented<NutritionFarmView>
            aria-label={NUTRITION_FARM_SECTION_TITLE}
            className="insights-lens-segmented w-fit"
            value={nutritionFarmView}
            options={FARM_VIEW_OPTIONS}
            onChange={(value) => dispatch(setNutritionFarmView(value))}
          />
        </ConfigProvider>
        <WorkspaceSection
          id="nutrition-farm-balance"
          title={NUTRITION_FARM_SECTION_TITLE}
          subtitle={tableSubtitle}
          actions={helpTip}
        >
          <FarmNutritionTable
            audit={audit}
            view={nutritionFarmView}
            nutrients={nutritionNutrients}
            trendNutrient={nutritionTrendNutrient}
            edit={edit}
            yieldLabel={yieldLabel}
            showCropDetails={nutritionShowCropDetails}
          />
        </WorkspaceSection>
      </div>
    );
  };

  const chartNutrients = isPaddockScope
    ? paddockNutrients
    : nutritionFarmView === NUTRITION_FARM_VIEW_SEASONS
      ? [nutritionTrendNutrient]
      : nutritionNutrients;
  const seasonTotals = computeSeasonTotals(
    paddock ? [paddock] : scopePaddocks,
    audit.seasons
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <ControlLabel>{NUTRITION_SEASONS_LABEL}</ControlLabel>
          <Segmented<NutritionSeasonRange>
            aria-label={NUTRITION_SEASONS_LABEL}
            value={nutritionSeasonRange}
            options={SEASON_RANGE_OPTIONS}
            onChange={(value) => dispatch(setNutritionSeasonRange(value))}
          />
          <span className="text-sm text-gray-muted">
            {audit.seasons.length > 1
              ? `${audit.seasons[0]}–${audit.seasons[audit.seasons.length - 1]}`
              : audit.seasons[0]}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <ControlLabel>{NUTRITION_DATA_BASIS_LABEL}</ControlLabel>
          <Segmented<InputTaskCategory>
            aria-label={NUTRITION_DATA_BASIS_LABEL}
            value={nutritionCategory}
            options={CATEGORY_OPTIONS}
            onChange={(value) => dispatch(setNutritionCategory(value))}
          />
        </div>
        {isPlan && (
          <div className="flex items-center gap-2">
            <ControlLabel>{NUTRITION_YIELD_SCENARIO_LABEL}</ControlLabel>
            <Segmented<AnalysisProductionScenario>
              aria-label={NUTRITION_YIELD_SCENARIO_LABEL}
              value={nutritionYieldScenario}
              options={YIELD_SCENARIO_OPTIONS}
              onChange={(value) => dispatch(setNutritionYieldScenario(value))}
            />
          </div>
        )}
        {isFarmScope &&
          (nutritionFarmView === NUTRITION_FARM_VIEW_BALANCE ? (
            <div className="flex min-w-[20rem] flex-1 items-center gap-2">
              <ControlLabel>{NUTRITION_NUTRIENTS_LABEL}</ControlLabel>
              <Select<NutrientKey[]>
                mode="multiple"
                aria-label={NUTRITION_NUTRIENTS_LABEL}
                className="min-w-[14rem] max-w-[36rem] flex-1"
                value={nutritionNutrients}
                options={NUTRIENT_OPTIONS}
                onChange={(value) =>
                  // Keep display order (N, P, K, S, …) whatever order they were picked in.
                  dispatch(
                    setNutritionNutrients(
                      NUTRIENT_DISPLAY_ORDER.filter((key) =>
                        value.includes(key)
                      )
                    )
                  )
                }
              />
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <ControlLabel>{NUTRITION_NUTRIENT_LABEL}</ControlLabel>
              <Select<NutrientKey>
                aria-label={NUTRITION_NUTRIENT_LABEL}
                className="w-44"
                value={nutritionTrendNutrient}
                options={NUTRIENT_OPTIONS}
                onChange={(value) => dispatch(setNutritionTrendNutrient(value))}
              />
            </div>
          ))}
        {/* Area and yield only appear beside each year's crop in the year view. */}
        {isFarmScope && nutritionFarmView === NUTRITION_FARM_VIEW_SEASONS && (
          <div className="flex items-center gap-2">
            <ControlLabel>{NUTRITION_SHOW_LABEL}</ControlLabel>
            <Checkbox
              checked={nutritionShowCropDetails}
              onChange={(event) =>
                dispatch(setNutritionShowCropDetails(event.target.checked))
              }
            >
              {NUTRITION_SHOW_CROP_DETAILS_LABEL}
            </Checkbox>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-4 xl:flex-row xl:items-start">
        <div className="min-w-0 flex-1">{renderMain()}</div>
        {!chartsOpen ? (
          // Collapsed: a slim rail on the right on extra-wide screens, a
          // plain button under the table otherwise — the table gets the room.
          <Button
            type="button"
            variant="secondary"
            size="sm"
            aria-label={NUTRITION_CHARTS_SHOW_LABEL}
            title={NUTRITION_CHARTS_SHOW_LABEL}
            className="inline-flex items-center gap-2 self-start px-2 xl:flex-col xl:py-3"
            onClick={() => setChartsOpen(true)}
          >
            <LuChevronsLeft size={16} aria-hidden />
            <span className="xl:[writing-mode:vertical-rl]">
              {NUTRITION_CHARTS_LABEL}
            </span>
          </Button>
        ) : (
          <>
            {/* Drag (or arrow keys) to resize the charts; double-click resets. Side by side on extra-wide screens only. */}
            <div
              role="separator"
              aria-orientation="vertical"
              aria-label={NUTRITION_CHART_PANEL_RESIZE_LABEL}
              aria-valuemin={NUTRITION_CHART_PANEL_MIN_WIDTH_REM}
              aria-valuemax={NUTRITION_CHART_PANEL_MAX_WIDTH_REM}
              aria-valuenow={Math.round(chartWidth)}
              tabIndex={0}
              title={NUTRITION_CHART_PANEL_RESIZE_LABEL}
              onMouseDown={onChartResizeStart}
              onDoubleClick={() =>
                setChartWidth(NUTRITION_CHART_PANEL_DEFAULT_WIDTH_REM)
              }
              onKeyDown={(event) => {
                // The handle is on the panel's left edge, so left widens it.
                if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
                  event.preventDefault();
                  const step =
                    event.key === 'ArrowLeft'
                      ? NUTRITION_CHART_PANEL_KEY_STEP_REM
                      : -NUTRITION_CHART_PANEL_KEY_STEP_REM;
                  setChartWidth(clampChartWidth(chartWidth + step));
                }
              }}
              className="group relative hidden w-3 flex-shrink-0 cursor-col-resize items-center justify-center self-stretch outline-none xl:flex"
            >
              {/* A visible divider line, darker on hover or focus, so it reads as something to drag. */}
              <span className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-gray-medium group-hover:w-0.5 group-hover:bg-gray-dark group-focus-visible:w-0.5 group-focus-visible:bg-gray-dark" />
              <span className="relative rounded bg-white py-1">
                <ResizeHandle />
              </span>
            </div>
            <div
              ref={chartPanelRef}
              // Runtime width from the drag, consumed by .nutrition-chart-panel (App.css) on extra-wide screens.
              style={
                {
                  '--nutrition-chart-width': `${chartWidth}rem`,
                } as React.CSSProperties
              }
              className="nutrition-chart-panel flex flex-col gap-4 xl:shrink-0"
            >
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="inline-flex items-center gap-1 px-2"
                  onClick={() => setChartsOpen(false)}
                >
                  {NUTRITION_CHARTS_HIDE_LABEL}
                  <LuChevronsRight size={16} aria-hidden />
                </Button>
              </div>
              {/* The overview first — the running balance (paddock) or area by
                  status (farm) — then the year-by-year detail under it. */}
              {isPaddockScope ? (
                <SectionCard
                  id="nutrition-chart-running-balance"
                  variant="result"
                  title={NUTRITION_CHART_RUNNING_BALANCE_TITLE}
                >
                  <RunningBalanceChart
                    paddock={paddock}
                    nutrients={chartNutrients}
                  />
                </SectionCard>
              ) : (
                <SectionCard
                  id="nutrition-chart-area-status"
                  variant="result"
                  title={NUTRITION_CHART_AREA_BY_STATUS_TITLE}
                >
                  <AreaByStatusChart
                    audit={audit}
                    nutrients={chartNutrients.filter(
                      (key) => NUTRIENT_REMOVAL_KEY[key] != null
                    )}
                  />
                </SectionCard>
              )}
              <SectionCard
                id="nutrition-chart-applied-removed"
                variant="result"
                title={NUTRITION_CHART_APPLIED_VS_REMOVED_TITLE}
              >
                <AppliedRemovedBySeasonChart
                  totals={seasonTotals}
                  nutrients={chartNutrients}
                />
              </SectionCard>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default NutritionView;
